/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/ban-types */
import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Product } from '@challenger6/vm-pattern-library/product-card/models/product.model';

declare global {
  interface Window {
    dataLayer: any[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  /**
   * Public method to add the dataLayer
   * @param event - the event name you would like to add
   * @param dataLayerObject - the new properties you would like to add
   */
  addDataLayer(event: string, dataLayerObject: any): void {
    this.addEventToDataLayer(event, dataLayerObject);
  }

  /**
   * Public method used to get the current data in the dataLayer
   */
  public getCurrentDataLayer(): object[] {
    return window.dataLayer;
  }

  public trackPageView(currentAppArea?: string, pageId?: string, level?: number) {
    if (isPlatformBrowser(this.platformId)) {
      const pageViewData = this.getPageData(currentAppArea, pageId, level);
      this.addEventToDataLayer('pageview', pageViewData);
    }
  }

  public trackPageAndCustomData(currentAppArea?: string, pageId?: string, level?: number, customData?: any) {
    let pageViewData = null;
    if (isPlatformBrowser(this.platformId)) {
      pageViewData = this.getPageData(currentAppArea, pageId, level);
    }
    pageViewData = {
      ...pageViewData,
      page: {
        ...pageViewData.page,
        pageType: pageId,
        // @ts-ignore
        pageModDate: null,
        journey: currentAppArea,
        pink: 'Non Pink',
      },
    };
    const trackingData = {
      ...pageViewData,
      ...customData,
    };
    this.addEventToDataLayer('pageview', trackingData);
  }

  public trackAccordionOpen(accordionTitle: string): void {
    this.addEventToDataLayer('accordion_open', {
      event: 'accordion_open',
      eventCategory: 'accordion interactions',
      eventAction: 'accordion open',
      eventLabel: accordionTitle,
    });
  }

  public trackAccordionClose(accordionTitle: string): void {
    this.addEventToDataLayer('accordion_close', {
      event: 'accordion_close',
      eventCategory: 'accordion interactions',
      eventAction: 'accordion close',
      eventLabel: accordionTitle,
    });
  }

  public generateHtml(productHtml) {
    const divEle = document.createElement('div');
    divEle.innerHTML = productHtml;
    divEle.className = 'product-html';
    const stikeThro = divEle.querySelectorAll('.product-html s');
    stikeThro.forEach((e) => e.remove());
    let finalHtml = '';
    const liEle = divEle.querySelectorAll('.product-html li');
    liEle.forEach((g) => (finalHtml += g.textContent.trim() + '|'));
    finalHtml = finalHtml.substring(0, finalHtml.length - 1);
    return finalHtml?.toLowerCase();
  }

  public trackProductImpressions(products: Product[], listName?: string): void {
    const productImpressions = [];
    products.forEach((product, index) => {
      const dimension1 =
        `${product.product?.toLowerCase()}|virgin media wifi hub` +
        `${product.tvKeyFeatures ? '|tv 360 box' : ''}${product.voltBenefits?.length ? '|volt benefits' : ''}${
          product.html?.length ? '|' + this.generateHtml(product.html) : ''
        }`;

      productImpressions.push({
        position: index + 1,
        id: product.regularOffer?.virginLongTermDiscounts?.toLowerCase(),
        name: product.championBrand?.toLowerCase(),
        price: product.regularOffer.recurringDiscountedPrice.toFixed(2),
        category: this.getCategory(product),
        brand: 'cable',
        list: listName || 'product listing',
        dimension1: dimension1?.toLowerCase(), //bundle component names
        dimension2: product.regularOffer?.virginProductCodes?.toLowerCase(), //bundle component product codes
        dimension3: product.displayValue?.toLowerCase(), //bundle TV component names
        dimension4: product.bandwidthLabel?.toLowerCase(), //bundle broadband component names
        dimension5: product.phoneProduct?.toString().toLowerCase(), //bundle Phone component names
        dimension6: 'n/a', //bundle SIM component names
        dimension7: 'n/a', //bundle handset component names
        dimension8: 'n/a', //bundle other component names
        dimension9: product.regularOffer?.recurringRegularPrice.toFixed(2).toLowerCase(), // bundle on-going prices after tenure
        dimension10: product.regularOffer?.onetimeRegularPrice.toFixed(2).toLowerCase(), // bundle setup costs  (Activation only)
        dimension11: '0.00', // bundle upfront costs (expect to be paid during checkout)
        dimension12: product.regularOffer?.recurringDiscountDuration.toString().toLowerCase(), // bundle contract tenure
        dimension13: 'false', // product insurance attached (mobiles handsets)
        dimension14: 'n/a', // product stock
        dimension15: 'false', // ESIM included
        dimension16: 'n/a', // tariff 4G or 5G
        dimension17: product.id.toString(),
        metric1: products.length, // count of product impressions
      });
    });

    this.addEventToDataLayer('productImpression', {
      ecommerce: {
        impressions: productImpressions,
      },
    });
  }

  public getCategory(product: Product): string {
    if (
      product.virginProductTypes.includes('TV') &&
      product.virginProductTypes.includes('BROADBAND') &&
      product.virginProductTypes.includes('PHONE')
    ) {
      return 'cable/triple';
    } else if (
      product.virginProductTypes.includes('BROADBAND') &&
      (product.virginProductTypes.includes('TV') || product.virginProductTypes.includes('PHONE'))
    ) {
      return 'cable/dual';
    } else if (
      product.virginProductTypes.includes('BROADBAND') ||
      product.virginProductTypes.includes('TV') ||
      product.virginProductTypes.includes('PHONE')
    ) {
      return 'cable/solus';
    }
  }

  updatePageTypeForPageViewEvent(pageType: String) {
    this.updatePageForPageViewEvent({ pageType });
  }

  updatePageForPageViewEvent(pageObject: any) {
    this.updateDataLayer('pageview', pageObject, 'page');
  }

  addClickEventToDataLayer(
    eventCategory: string,
    eventLabel: string,
    eventAction: string = 'CTA_clicked',
    event: string = 'siteInteraction'
  ) {
    this.addDataLayer(event, {
      eventAction,
      eventCategory,
      eventLabel,
    });
  }

  /**
   * Used to update an existing event in the dataLayer with new properties
   * @param event - the event name you would like to edit
   * @param newObject - the new properties you would like to add or replace
   * @param propertyToUpdate - Optional use in case if you want to only change a specific property and don't want to override the whole object
   */
  public updateDataLayer(event: string, newObject: object, propertyToUpdate?: string): void {
    let dataLayerWasUpdated = false;

    if (window.dataLayer && window.dataLayer.length) {
      window.dataLayer.forEach((dataLayerItem: any, dataLayerItemIndex: number) => {
        if (event === dataLayerItem?.event) {
          if (propertyToUpdate) {
            dataLayerItem[propertyToUpdate] = {
              ...dataLayerItem[propertyToUpdate],
              ...newObject,
            };
          } else {
            dataLayerItem = {
              ...dataLayerItem,
              ...newObject,
            };
          }
          /**
            IMPORTANT!
            If we do not use the push method the event does not propagate to the
            Google tag manager.
            After each update we remove the old event from the dataLayer array to
            prevent memory leaks,giant dataLayer object.
            Everytime we push an existing event gtn adds a breakpoint in the array
            with the name of the event and this is the reason we remove 2 items from
            the dataLayer.
           */
          window.dataLayer.push(event, dataLayerItem);
          window.dataLayer.splice(dataLayerItemIndex, 2);
          dataLayerWasUpdated = true;
        }
      });
    }

    if (!dataLayerWasUpdated) {
      this.addEventToDataLayer(event, newObject);
    }
  }

  /**
   * Used to update the dataLayer with a new event that does not yet exist in the current dataLayer
   * @param event - the event name you would like to edit
   * @param dataLayerObject - the new properties you would like to add
   */
  private addEventToDataLayer(event: string, dataLayerObject: object): void {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    window.dataLayer.push({
      ...dataLayerObject,
      event,
    });
  }

  private getPageData(currentAppArea?: string, pageId?: string, level?: number) {
    const cleanHostname = location.hostname.replace('www.', '');
    const subDomainRegex = /^([\w-]+)(?:\.[\w-]+){2,}$/;
    const subdomain = subDomainRegex.test(cleanHostname) ? cleanHostname.match(subDomainRegex)[1] : '';

    return {
      page: {
        currentTimestamp: Date.now(),
        embeddedWebApp: 'false',
        environment: window.location.href.indexOf('virginmedia.com') !== -1 ? 'prod' : 'dev',
        pageReferrer: window.document.referrer,
        pageUrl: window.location.href,
        pageID: pageId,
        pageType: `${currentAppArea}${level ? ' | level' + level : ''}`,
        path: window.location.pathname,
        platform: 'AngularWeb',
        query: window.location.search,
        sessionReferrer: sessionStorage.getItem('sessionReferrerUrl'),
        subdomain,
        pagePath: currentAppArea,
      },
    };
  }
}
